import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { CalendarBlank, Package, PaperPlaneTilt, HardHat, ArrowRight, CheckCircle, Plus, Minus, X, MagnifyingGlass, NotePencil, MapPin } from "@phosphor-icons/react";
import api, { publicAssetUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { buyerContactFromUser } from "@/lib/buyerProfile";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const DEFAULT_GRID_PRODUCTS = 11;

const newLineSpec = () => ({
  quantity: 1,
});

const qtyBtnClass =
  "w-8 h-8 flex items-center justify-center border border-neutral-300 rounded-sm bg-white hover:bg-neutral-100 text-neutral-800 shrink-0";

function ProductQuantityControl({ value, onChange, onDecrement, onIncrement }) {
  const [draft, setDraft] = useState(String(value));

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = (raw) => {
    const digits = String(raw).replace(/\D/g, "");
    const n = digits === "" ? 1 : parseInt(digits, 10);
    const q = Math.min(9999, Math.max(1, n));
    onChange(q);
    setDraft(String(q));
  };

  return (
    <div className="flex items-center gap-1 shrink-0" role="group" aria-label="Quantity">
      <button type="button" onClick={onDecrement} className={qtyBtnClass} aria-label="Decrease quantity">
        <Minus size={14} weight="bold" />
      </button>
      <input
        type="text"
        inputMode="numeric"
        aria-label="Quantity"
        value={draft}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, "");
          setDraft(digits);
          if (digits !== "") {
            const n = parseInt(digits, 10);
            if (n >= 1) onChange(Math.min(9999, n));
          }
        }}
        onBlur={() => commit(draft)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit(draft);
            e.target.blur();
          }
        }}
        className="w-10 h-8 border border-neutral-300 rounded-sm bg-white text-center text-sm font-semibold text-neutral-900 tabular-nums leading-none outline-none focus:border-neutral-400 focus:ring-1 focus:ring-neutral-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      <button type="button" onClick={onIncrement} className={qtyBtnClass} aria-label="Increase quantity">
        <Plus size={14} weight="bold" />
      </button>
    </div>
  );
}

export default function SellPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [products, setProducts] = useState([]);
  const [categoryNames, setCategoryNames] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState({});
  const [catalogSearchOpen, setCatalogSearchOpen] = useState(false);
  const [catalogSearchQuery, setCatalogSearchQuery] = useState("");
  const [catalogSearchCategory, setCatalogSearchCategory] = useState("__all__");
  const catalogSearchInputRef = useRef(null);

  const [pickupPoint, setPickupPoint] = useState("");
  const [pickupDate, setPickupDate] = useState("");
  const [phone, setPhone] = useState("");
  const [sellerName, setSellerName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    const contact = buyerContactFromUser(user);
    if (!contact) return;
    setPhone((p) => p || contact.phone);
    setSellerName((n) => n || contact.buyerName);
    setPickupPoint((pp) => pp || contact.pickupPoint);
  }, [authLoading, user?.id]);

  useEffect(() => {
    api
      .get("/products", { params: { limit: 200 } })
      .then((r) => {
        const raw = r.data.items ?? r.data;
        setProducts(Array.isArray(raw) ? raw : []);
      })
      .catch(() => setProducts([]));
    api
      .get("/public/product-category-names")
      .then((r) => setCategoryNames(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCategoryNames([]));
  }, []);

  useEffect(() => {
    if (!catalogSearchOpen) return;
    const t = requestAnimationFrame(() => catalogSearchInputRef.current?.focus());
    return () => cancelAnimationFrame(t);
  }, [catalogSearchOpen]);

  const catalogCategoryOptions = useMemo(() => {
    const set = new Set();
    for (const c of categoryNames) {
      const v = String(c || "").trim();
      if (v) set.add(v);
    }
    for (const p of products) {
      const c = String(p?.category || "").trim();
      if (c) set.add(c);
    }
    const arr = Array.from(set);
    arr.sort((a, b) => String(a).localeCompare(String(b)));
    return ["__all__", ...arr];
  }, [categoryNames, products]);

  const gridProducts = useMemo(
    () => products.slice(0, DEFAULT_GRID_PRODUCTS),
    [products]
  );

  const catalogSearchResults = useMemo(() => {
    const q = catalogSearchQuery.trim().toLowerCase();
    const cat = String(catalogSearchCategory || "__all__");
    const pool =
      cat === "__all__"
        ? products
        : products.filter((p) => String(p?.category || "").trim() === cat);
    if (!q) return pool;
    return pool.filter((p) => String(p?.name || "").toLowerCase().includes(q));
  }, [catalogSearchQuery, catalogSearchCategory, products]);

  const toggleProduct = (product) => {
    setSelectedProducts((prev) => {
      const next = { ...prev };
      if (next[product.id]) {
        delete next[product.id];
      } else {
        next[product.id] = { ...product, ...newLineSpec() };
      }
      return next;
    });
  };

  const incrementProduct = (p) => {
    setSelectedProducts((prev) => {
      const next = { ...prev };
      const existing = next[p.id];
      if (!existing) {
        next[p.id] = { ...p, ...newLineSpec() };
        return next;
      }
      next[p.id] = { ...existing, quantity: Math.min(9999, (existing.quantity || 1) + 1) };
      return next;
    });
  };

  const decrementProduct = (p) => {
    setSelectedProducts((prev) => {
      const next = { ...prev };
      const existing = next[p.id];
      if (!existing) return prev;
      const q = (existing.quantity || 1) - 1;
      if (q <= 0) {
        delete next[p.id];
        return next;
      }
      next[p.id] = { ...existing, quantity: q };
      return next;
    });
  };

  const setProductQuantity = (p, quantity) => {
    setSelectedProducts((prev) => {
      const existing = prev[p.id];
      if (!existing) return prev;
      const q = Math.min(9999, Math.max(1, Number(quantity) || 1));
      return { ...prev, [p.id]: { ...existing, quantity: q } };
    });
  };

  const addCustomProductFromCatalog = () => {
    const name = catalogSearchQuery.trim();
    if (!name) return;
    const id = `custom-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setSelectedProducts((prev) => ({
      ...prev,
      [id]: {
        id,
        name,
        category: "Custom",
        image_url: "",
        isCustom: true,
        ...newLineSpec(),
      },
    }));
    setCatalogSearchQuery("");
    toast.success("Added custom product");
  };

  const submitSell = async () => {
    const lines = Object.values(selectedProducts);
    if (lines.length === 0) {
      toast.error("Select at least one product");
      return;
    }
    if (!sellerName.trim()) {
      toast.error("Enter your name");
      return;
    }
    if (!phone.trim() || phone.trim().length < 10) {
      toast.error("Enter a valid phone number");
      return;
    }
    if (!pickupPoint.trim()) {
      toast.error("Enter pickup point");
      return;
    }
    if (!pickupDate) {
      toast.error("Select pickup date");
      return;
    }

    const items = lines.map((p) => ({
      product_name: p.name,
      product_id: p.isCustom ? null : p.id,
      spec_type: "quantity",
      quantity: Math.max(1, parseInt(String(p.quantity || 1), 10) || 1),
      area_value: null,
      area_unit: null,
    }));

    setSubmitting(true);
    try {
      await api.post("/leads", {
        buyer_phone: phone.trim(),
        buyer_name: sellerName.trim(),
        buyer_company: "",
        city: pickupPoint.trim(),
        start_date: pickupDate,
        duration: "N/A",
        requirement_type: "sell",
        items,
        source: "platform",
      });
      toast.success("Sell request submitted!");
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F3F4F6]">
        <Card className="max-w-md w-full mx-4 rounded-sm border-neutral-200 shadow-[4px_4px_0px_#0A0A0A]">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-fuchsia-100 flex items-center justify-center rounded-sm">
              <PaperPlaneTilt size={32} weight="bold" className="text-fuchsia-700" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Sell Request Submitted</h2>
            <p className="text-neutral-600 mb-6">
              Our team will contact you to confirm pickup details.
            </p>
            <Button
              data-testid="sell-submit-another-btn"
              onClick={() => {
                setSubmitted(false);
                setSelectedProducts({});
                setPhone("");
                setSellerName("");
                setPickupPoint("");
                setPickupDate("");
              }}
              className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm w-full"
            >
              Submit Another Sell Request
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const selectedCount = Object.keys(selectedProducts).length;

  return (
    <div className="min-h-screen bg-white">
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-neutral-200" data-testid="sell-navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-0 sm:h-16 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <HardHat size={28} weight="bold" className="text-orange-600" />
            <span className="text-xl font-bold tracking-tight">SCAFFOINDIA</span>
            <Badge className="rounded-sm bg-fuchsia-100 text-fuchsia-900 border border-fuchsia-300 text-xs ml-2">
              SELL
            </Badge>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
            <Button variant="outline" asChild className="w-full sm:w-auto rounded-sm border-neutral-300 hover:bg-neutral-100 text-sm">
              <Link to="/" data-testid="sell-back-home-btn">
                Back to requirements
              </Link>
            </Button>
          </div>
        </div>
      </nav>

      <section className="py-12 bg-[#F3F4F6]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="overline mb-3">Sell used products</div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">What used equipment are you selling?</h2>
          <p className="text-neutral-500 text-sm mb-8">
            Select used items from the catalog (or add custom lines), set quantity, then enter pickup details.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
            {gridProducts.map((p) => {
              const isSelected = !!selectedProducts[p.id];
              return (
                <div
                  key={p.id}
                  data-testid={`sell-product-card-${p.id}`}
                  onClick={() => toggleProduct(p)}
                  className={`cursor-pointer rounded-sm border-2 transition-all overflow-hidden ${
                    isSelected
                      ? "border-orange-500 shadow-[3px_3px_0px_#FF5A1F] bg-orange-50"
                      : "border-neutral-200 hover:border-neutral-400 bg-white"
                  }`}
                >
                  <div className="relative aspect-square bg-neutral-100">
                    {p.image_url ? (
                      <img src={publicAssetUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package size={40} className="text-neutral-300" />
                      </div>
                    )}
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-7 h-7 bg-orange-600 rounded-sm flex items-center justify-center">
                        <CheckCircle size={18} weight="fill" className="text-white" />
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-bold leading-tight">{p.name}</p>
                    <p className="text-xs text-neutral-500 mt-0.5">{p.category}</p>
                  </div>
                </div>
              );
            })}

            <button
              type="button"
              data-testid="sell-search-products-card"
              onClick={() => {
                setCatalogSearchQuery("");
                setCatalogSearchCategory("__all__");
                setCatalogSearchOpen(true);
              }}
              className="cursor-pointer rounded-sm border-2 border-dashed border-neutral-300 hover:border-orange-400 bg-white flex flex-col items-center justify-center min-h-[160px] sm:min-h-[200px] transition-all text-left w-full"
            >
              <MagnifyingGlass size={32} className="text-orange-500 mb-2" weight="bold" />
              <p className="text-sm font-bold text-neutral-800">Search products</p>
              <p className="text-xs text-neutral-500 mt-0.5 px-3 text-center">Add from full catalog</p>
            </button>
          </div>

          {selectedCount > 0 ? (
            <Card className="rounded-sm border-neutral-200 mb-6">
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <p className="font-bold">Selected products</p>
                  <Badge variant="secondary" className="rounded-sm">{selectedCount} items</Badge>
                </div>
                <div className="space-y-2">
                  {Object.values(selectedProducts).map((p) => (
                    <div key={p.id} className="flex items-center justify-between gap-2 border border-neutral-200 rounded-sm p-2 bg-neutral-50">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{p.name}</p>
                        <p className="text-xs text-neutral-500">{p.category}</p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <ProductQuantityControl
                          value={p.quantity || 1}
                          onChange={(q) => setProductQuantity(p, q)}
                          onDecrement={() => decrementProduct(p)}
                          onIncrement={() => incrementProduct(p)}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedProducts((prev) => {
                              const next = { ...prev };
                              delete next[p.id];
                              return next;
                            })
                          }
                          className="w-8 h-8 flex items-center justify-center border border-red-300 rounded-sm bg-white hover:bg-red-50 text-red-700"
                          aria-label="Remove"
                        >
                          <X size={16} weight="bold" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : null}

          <Card className="rounded-sm border-neutral-200">
            <CardContent className="p-4 space-y-4">
              {user?.role !== "buyer" ? (
                <p className="text-xs text-neutral-500">
                  <Link to="/account/login?next=/sell" className="text-orange-600 font-semibold hover:underline">
                    Sign in
                  </Link>{" "}
                  to auto-fill your contact details.
                </p>
              ) : null}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-mono uppercase text-neutral-500">Your name</label>
                  <Input className="rounded-sm border-neutral-300 mt-1" value={sellerName} onChange={(e) => setSellerName(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase text-neutral-500">Phone</label>
                  <Input className="rounded-sm border-neutral-300 mt-1" value={phone} onChange={(e) => setPhone(e.target.value)} />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-mono uppercase text-neutral-500 flex items-center gap-1">
                    <MapPin size={14} /> Pickup point
                  </label>
                  <Input className="rounded-sm border-neutral-300 mt-1" value={pickupPoint} onChange={(e) => setPickupPoint(e.target.value)} placeholder="Address / landmark" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-mono uppercase text-neutral-500 flex items-center gap-1">
                    <CalendarBlank size={14} /> Pickup date
                  </label>
                  <Input className="rounded-sm border-neutral-300 mt-1" type="date" value={pickupDate} onChange={(e) => setPickupDate(e.target.value)} />
                </div>
              </div>
              <Button
                type="button"
                disabled={submitting}
                onClick={submitSell}
                className="w-full rounded-sm bg-orange-600 hover:bg-orange-700 text-white font-bold"
                data-testid="sell-submit-btn"
              >
                {submitting ? "Submitting…" : "Submit sell request"}
                <ArrowRight size={18} weight="bold" className="ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      <Dialog
        open={catalogSearchOpen}
        onOpenChange={(open) => {
          setCatalogSearchOpen(open);
          if (!open) {
            setCatalogSearchQuery("");
            setCatalogSearchCategory("__all__");
          }
        }}
      >
        <DialogContent className="max-w-lg rounded-sm border-neutral-200 sm:rounded-sm gap-0 p-0 overflow-hidden" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader className="p-6 pb-4 space-y-1.5">
            <DialogTitle className="text-left">Search catalog</DialogTitle>
            <DialogDescription className="text-left">
              Filter by category and search by name. If nothing matches, add a custom product from your search text.
            </DialogDescription>
          </DialogHeader>
          <div className="px-6 pb-2">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="sm:col-span-1">
                <Select
                  value={catalogCategoryOptions.includes(catalogSearchCategory) ? catalogSearchCategory : "__all__"}
                  onValueChange={(v) => setCatalogSearchCategory(v)}
                >
                  <SelectTrigger className="rounded-sm border-neutral-300" data-testid="sell-catalog-category-select">
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All categories</SelectItem>
                    {catalogCategoryOptions
                      .filter((c) => c !== "__all__")
                      .map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="relative sm:col-span-2">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" size={18} weight="bold" />
                <Input
                  ref={catalogSearchInputRef}
                  data-testid="sell-catalog-search-input"
                  placeholder="Search by name…"
                  value={catalogSearchQuery}
                  onChange={(e) => setCatalogSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    e.preventDefault();
                    if (catalogSearchResults.length === 0 && catalogSearchQuery.trim()) {
                      addCustomProductFromCatalog();
                    }
                  }}
                  className="pl-10 rounded-sm border-neutral-300"
                />
              </div>
            </div>
          </div>
          <div className="border-t border-neutral-200 bg-neutral-50 max-h-[min(50vh,320px)] overflow-y-auto mx-0">
            {catalogSearchResults.length === 0 ? (
              <div className="p-4 text-center space-y-3">
                <p className="text-sm text-neutral-600 font-medium">No products match your search.</p>
                {catalogSearchQuery.trim() ? (
                  <>
                    <p className="text-xs text-neutral-500">Add this as a custom product using the name you typed above.</p>
                    <Button
                      type="button"
                      onClick={addCustomProductFromCatalog}
                      className="rounded-sm bg-orange-600 hover:bg-orange-700 text-white font-bold w-full sm:w-auto"
                      data-testid="sell-catalog-add-custom-btn"
                    >
                      <NotePencil size={16} weight="bold" className="mr-1.5" />
                      Add &quot;{catalogSearchQuery.trim()}&quot;
                    </Button>
                  </>
                ) : (
                  <p className="text-xs text-neutral-500">Try another search term.</p>
                )}
              </div>
            ) : (
              <ul className="py-1">
                {catalogSearchResults.map((p) => {
                  const inCart = selectedProducts[p.id];
                  return (
                    <li key={p.id}>
                      <div className="w-full flex items-center gap-3 px-4 py-3 border-b border-neutral-100/80 last:border-0">
                        <div className="w-11 h-11 rounded-sm overflow-hidden flex-shrink-0 bg-neutral-200">
                          {p.image_url ? (
                            <img src={publicAssetUrl(p.image_url)} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-neutral-200">
                              <Package size={22} className="text-neutral-400" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1 text-left">
                          <p className="text-sm font-bold leading-tight truncate">{p.name}</p>
                          <p className="text-xs text-neutral-500">{p.category}</p>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {inCart ? (
                            <ProductQuantityControl
                              value={inCart.quantity || 1}
                              onChange={(q) => setProductQuantity(p, q)}
                              onDecrement={() => decrementProduct(p)}
                              onIncrement={() => incrementProduct(p)}
                            />
                          ) : (
                            <button
                              type="button"
                              onClick={() => incrementProduct(p)}
                              className={qtyBtnClass}
                              aria-label="Add product"
                            >
                              <Plus size={14} weight="bold" />
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <DialogFooter className="p-4 border-t border-neutral-200 bg-white">
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => setCatalogSearchOpen(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}

